// Quality.GoalCoverage — how a chat's materials meet its goals (Kontext goal,
// .claude/analysis/kontext-goal-2026-10-06/README.md).
//
// A goal is a LearningContext the chat answers to (`Conversation.Grounding`,
// several per chat). For each goal this fold reads, from state alone:
//
//   - the requirement documents it answers to  (KB: context answers-to source);
//   - its objectives, each with where it comes from in a requirement document
//     (KB: objective answers-to source passage), the material parts that
//     EXPLAIN it (KB: part explains objective) and the questions that PRACTISE
//     it (KB: part practises objective);
//   - its required terms (the context's concepts, named in its instruction
//     language), each with the source's own definition (KB: term defined-in
//     source passage), the parts that DEFINE it (KB: part explains term) and
//     the parts that only USE it (a pure text match over the material).
//
// Scope: the `roots` are the materials measured (the chat's materials, or one
// of them); a material a root includes (a generated part) counts for that
// root. A link to a part, objective, term or source that no longer resolves is
// dropped, never shown as coverage.
//
// Status (owner 2026-10-06): an objective is MET when it is explained AND
// practised, PARTLY when only one of the two, NOT YET when nothing serves it.
// A term is DEFINED when some part explains it, USED when it only occurs,
// MISSING when it does not occur. `objectiveStatus` / `termStatus` are the one
// home of those rules (the client re-derives a single material's status from
// the same parts with them).
//
// Sub-goals (D56, .claude/analysis/top-down-authoring-2026-10-07/design.md): a
// goal is a projection of the workspace entities it references, and a goal
// may sit under another (KB: child broader parent). A goal shows its
// sub-goals nested (creation order); a chat goal that sits under another of
// the chat's goals shows only nested. A sub-goal shows its own objectives and
// terms; its audience (and the language its terms are named in) reads
// through from the nearest ancestor that sets one, until it sets its own,
// never copied. A goal's `status` rolls its own and its sub-goals' objectives
// up: met when all are met, partly when any is served. The sources a goal
// draws on (KB: goal draws-on source) are listed for the panel's Källor group.
//
// Concepts (D55 S10): each required term carries its agreed definition (the
// Term's value object) and the concept's links (`Quality.ConceptLinks`).
//
// Source passages stay `{sourceId, locator}` here: their page and words live
// in the source's extract, which the app resolves (I/O). Pure: no I/O, no casts.
export { goalStatus, goalsOf, objectiveStatus, objectivesIn, occursIn, parentGoalOf, partOf, termStatus, };
import { exhaustSwitchCase } from "@bilda/utils";
import { queryTriples } from "../knowledgeBank/queryTriples.js";
import * as RelationId from "../knowledgeBank/relationId.js";
import { liveBackedBy } from "../learning/term.js";
import * as Ast from "../resource/ast.js";
import { conceptLinksOf, termOf } from "./conceptLinks.js";
// === Status rules ===
// Generic over the part shape so a client can re-derive one material's
// status from the same parts it received over the wire.
const objectiveStatus = (explains, practises) => explains.length > 0 && practises.length > 0
    ? "met"
    : explains.length + practises.length > 0
        ? "partly"
        : "not_yet";
const termStatus = (defines, uses) => defines.length > 0 ? "defined" : uses.length > 0 ? "used" : "missing";
const objectivesIn = (goal) => [
    ...goal.objectives,
    ...goal.subGoals.flatMap((g) => objectivesIn(g)),
];
// The roll-up: met when every objective is met, partly when any is served,
// not yet otherwise (no objectives = not yet).
const goalStatus = (objectives) => objectives.length > 0 && objectives.every((o) => o.status === "met")
    ? "met"
    : objectives.some((o) => o.status !== "not_yet")
        ? "partly"
        : "not_yet";
// === Term occurrence ===
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
// Does `term` occur in `text`? Case-insensitive, at the START of a word, any
// ending: "funktion" occurs in "Funktionen" and "funktioner", "k-värde" in
// "k-värdet" (Swedish inflects by suffix). Never inside a word ("tion").
const occursIn = (term, text) => {
    const t = term.trim();
    if (t.length === 0)
        return false;
    return new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegExp(t)}`, "iu").test(text);
};
const EXCERPT_WORDS = 6;
const excerptOf = (text) => {
    const words = text.trim().split(/\s+/u);
    return words.length > EXCERPT_WORDS
        ? `${words.slice(0, EXCERPT_WORDS).join(" ")} …`
        : words.join(" ");
};
const placedIn = (nodes, section) => nodes.flatMap((node) => node.type === "section"
    ? [{ node, section }, ...placedIn(node.children, node.title)]
    : [{ node, section }]);
// How a node is named in a link; `undefined` for an include (its part is
// walked as a material of its own). A passage is named by its section.
const labelOf = ({ node, section }, questionOrdinal) => {
    switch (node.type) {
        case "section":
            return { type: "section", title: node.title };
        case "heading":
            return { type: "section", title: node.text };
        case "prose":
        case "list":
        case "media":
            return section === undefined
                ? {
                    type: "passage",
                    excerpt: excerptOf(Ast.nodeText(node).join(" ")),
                }
                : { type: "section", title: section };
        case "question_single":
            return { type: "question", ordinal: questionOrdinal };
        case "include":
            return undefined;
        default:
            return exhaustSwitchCase(node);
    }
};
// Every addressable node of one material, labelled, plus the parts it
// includes. A section's own text is its title (its children are parts).
const partsOfAst = (ast, rootId, materialId) => {
    const placed = placedIn(ast.body, undefined);
    const questionIds = placed.flatMap((p) => p.node.type === "question_single" ? [p.node.id] : []);
    const parts = placed.flatMap((p) => {
        const label = labelOf(p, questionIds.indexOf(p.node.id) + 1);
        return label === undefined
            ? []
            : [
                {
                    rootId,
                    materialId,
                    elementId: `${p.node.id}`,
                    label,
                    text: p.node.type === "section"
                        ? p.node.title
                        : Ast.nodeText(p.node).join("\n"),
                    isQuestion: p.node.type === "question_single",
                },
            ];
    });
    const includes = placed.flatMap((p) => p.node.type === "include" ? [p.node.target.id] : []);
    return { parts, includes };
};
// The measured parts of every root, keyed `materialId/elementId`. A material
// two roots both include counts for the first; include cycles stop.
const indexParts = (state, roots) => {
    const index = new Map();
    const seen = new Set();
    const visit = (rootId, materialId) => {
        if (seen.has(materialId))
            return;
        seen.add(materialId);
        const material = state.resources.resources.get(materialId);
        if (material?.type !== "material")
            return;
        const { parts, includes } = partsOfAst(Ast.astForRender(material.content), rootId, materialId);
        for (const p of parts)
            index.set(keyOf(p.materialId, p.elementId), p);
        for (const included of includes)
            visit(rootId, included);
    };
    for (const root of roots)
        visit(root, root);
    return index;
};
const keyOf = (materialId, elementId) => `${materialId}/${elementId}`;
const asPart = ({ rootId, materialId, elementId, label, }) => ({
    rootId,
    materialId,
    elementId,
    label,
});
// One part of a material (or of a part it includes), if it exists: what a
// coverage link may name. `isQuestion`: only a question practises.
const partOf = (state, materialId, elementId) => {
    const p = indexParts(state, [materialId]).get(keyOf(materialId, elementId));
    return p === undefined
        ? undefined
        : { ...asPart(p), isQuestion: p.isQuestion };
};
// === KB reads ===
// The measured part a link's subject names, if it is one.
const partOfSubject = (index, a) => a.subject.type === "part_resource_ref"
    ? index.get(keyOf(a.subject.id, `${a.subject.locator}`))
    : undefined;
// The first source passage an entity's links point to (a live source only).
const passageOf = (state, links) => links.flatMap((a) => a.object.type === "part_resource_ref" &&
    state.resources.resources.get(a.object.id)?.type === "source"
    ? [{ sourceId: a.object.id, locator: `${a.object.locator}` }]
    : [])[0];
const unique = (parts) => parts
    .filter((p, i) => parts.findIndex((q) => q.materialId === p.materialId &&
    q.elementId === p.elementId) === i)
    .map(asPart);
const nothingInherited = {
    audience: [],
    audienceFrom: undefined,
    language: undefined,
};
const audienceOf = (state, ctx) => [...ctx.learnerProfileRefs].flatMap((ref) => {
    const p = state.learning.learnerProfiles.get(ref.id);
    return p?.type === "active_learner_profile" ? [`${p.label}`] : [];
});
const goalOf = (state, ctx, index, links, inherited, visited) => {
    const linksOf = (triples, objectType, id) => triples.filter((a) => a.object.type === objectType && a.object.id === id);
    const partsLinkedTo = (triples, objectType, id) => linksOf(triples, objectType, id).flatMap((a) => {
        const p = partOfSubject(index, a);
        return p === undefined ? [] : [p];
    });
    const subjectLinks = (triples, subjectType, id) => triples.filter((a) => a.subject.type === subjectType && a.subject.id === id);
    const requirements = links.answersTo.flatMap((a) => {
        if (a.subject.type !== "learning_context_ref")
            return [];
        if (a.subject.id !== ctx.id)
            return [];
        if (a.object.type !== "whole_resource_ref")
            return [];
        const source = state.resources.resources.get(a.object.id);
        return source?.type === "source"
            ? [{ sourceId: source.id, title: `${source.title}` }]
            : [];
    });
    const objectives = [...ctx.objectiveRefs].flatMap((ref) => {
        const o = state.learning.objectives.get(ref.id);
        if (o?.type !== "active_objective")
            return [];
        const explains = unique(partsLinkedTo(links.explains, "objective_ref", o.id));
        const practises = unique(partsLinkedTo(links.practises, "objective_ref", o.id).filter((p) => p.isQuestion));
        return [
            {
                id: o.id,
                statement: `${o.statement}`,
                from: passageOf(state, subjectLinks(links.answersTo, "objective_ref", o.id)),
                explains,
                practises,
                status: objectiveStatus(explains, practises),
            },
        ];
    });
    const language = [...ctx.instructionLanguageProfileRefs][0]?.id ?? inherited.language;
    const terms = [...ctx.conceptRefs].flatMap((ref) => {
        const term = termOf(state, ref.id, language);
        if (term === undefined)
            return [];
        const definedParts = partsLinkedTo(links.explains, "term_ref", term.id);
        const defines = unique(definedParts);
        const uses = unique([...index.values()].filter((p) => occursIn(`${term.text}`, p.text) &&
            !definedParts.some((d) => d.materialId === p.materialId &&
                d.elementId === p.elementId)));
        return [
            {
                ...conceptLinksOf(state, ref.id, language),
                id: term.id,
                conceptId: ref.id,
                text: `${term.text}`,
                definition: term.definition?.prose,
                definitionClaims: term.definition === undefined
                    ? []
                    : liveBackedBy(state.knowledgeBank, term.definition),
                definedIn: passageOf(state, subjectLinks(links.definedIn, "term_ref", term.id)),
                defines,
                uses,
                status: termStatus(defines, uses),
            },
        ];
    });
    const ownAudience = audienceOf(state, ctx);
    const own = ownAudience.length > 0;
    const audience = own ? ownAudience : inherited.audience;
    const audienceFrom = own ? undefined : inherited.audienceFrom;
    const sources = links.drawsOn.flatMap((a) => {
        if (a.subject.type !== "learning_context_ref")
            return [];
        if (a.subject.id !== ctx.id)
            return [];
        if (a.object.type !== "whole_resource_ref")
            return [];
        const source = state.resources.resources.get(a.object.id);
        return source?.type === "source"
            ? [{ sourceId: source.id, title: `${source.title}` }]
            : [];
    });
    const passOn = {
        audience,
        audienceFrom: own
            ? { contextId: ctx.id, title: `${ctx.title}` }
            : audienceFrom,
        language,
    };
    const seen = new Set([...visited, `${ctx.id}`]);
    const subGoals = childrenOf(state, links, ctx.id)
        .filter((child) => !seen.has(`${child.id}`))
        .map((child) => goalOf(state, child, index, links, passOn, seen));
    return {
        contextId: ctx.id,
        title: `${ctx.title}`,
        purpose: ctx.purpose === undefined ? undefined : `${ctx.purpose}`,
        audience,
        audienceFrom,
        requirements,
        sources,
        objectives,
        terms,
        subGoals,
        status: goalStatus([
            ...objectives,
            ...subGoals.flatMap((g) => objectivesIn(g)),
        ]),
    };
};
// === The goal tree (KB: child broader parent) ===
const activeContext = (state, id) => {
    const ctx = state.learning.learningContexts.get(id);
    return ctx?.type === "active_learning_context" ? ctx : undefined;
};
// The live goal a goal sits under, if any (a deleted parent reads as none).
const parentIn = (state, broader, id) => broader.flatMap((a) => a.subject.type === "learning_context_ref" &&
    a.subject.id === id &&
    a.object.type === "learning_context_ref" &&
    activeContext(state, a.object.id) !== undefined
    ? [a.object.id]
    : [])[0];
// The goal one goal sits under (the agent's tools and the turn prompt).
const parentGoalOf = (state, id) => parentIn(state, queryTriples(state, { relation: RelationId.broader }), id);
// Every live sub-goal of a goal, in creation order (the contexts map's order).
const childrenOf = (state, links, id) => [...state.learning.learningContexts.values()].flatMap((ctx) => ctx.type === "active_learning_context" &&
    parentIn(state, links.broader, ctx.id) === id
    ? [ctx]
    : []);
// A goal's ancestors, nearest first (bounded: a cycle stops the walk).
const ancestorsOf = (state, links, id) => {
    const walk = (at, acc) => {
        const parent = parentIn(state, links.broader, at);
        return parent === undefined || parent === id || acc.includes(parent)
            ? acc
            : walk(parent, [...acc, parent]);
    };
    return walk(id, []);
};
// What a goal inherits: per value, the nearest ancestor that sets it.
const inheritedOf = (state, links, id) => ancestorsOf(state, links, id)
    .flatMap((a) => {
    const ctx = activeContext(state, a);
    return ctx === undefined ? [] : [ctx];
})
    .reduceRight((above, ctx) => {
    const audience = audienceOf(state, ctx);
    return {
        audience: audience.length > 0 ? audience : above.audience,
        audienceFrom: audience.length > 0
            ? { contextId: ctx.id, title: `${ctx.title}` }
            : above.audienceFrom,
        language: [...ctx.instructionLanguageProfileRefs][0]?.id ??
            above.language,
    };
}, nothingInherited);
// The goals (in the given order; a missing or deleted context is skipped),
// measured over `roots`.
const goalsOf = (state, contextIds, roots) => {
    const index = indexParts(state, roots);
    const byRelation = (relation) => queryTriples(state, { relation });
    const links = {
        answersTo: byRelation(RelationId.answersTo),
        definedIn: byRelation(RelationId.definedIn),
        explains: byRelation(RelationId.explains),
        practises: byRelation(RelationId.practises),
        drawsOn: byRelation(RelationId.drawsOn),
        broader: byRelation(RelationId.broader),
    };
    const live = contextIds.filter((id, i) => contextIds.indexOf(id) === i &&
        activeContext(state, id) !== undefined);
    // A goal under another of the given goals shows only nested.
    const top = live.filter((id) => !ancestorsOf(state, links, id).some((a) => live.includes(a)));
    return top.flatMap((id) => {
        const ctx = activeContext(state, id);
        return ctx === undefined
            ? []
            : [
                goalOf(state, ctx, index, links, inheritedOf(state, links, id), new Set()),
            ];
    });
};
//# sourceMappingURL=goalCoverage.js.map